import { Box } from '@mui/material';
import ProtectedEmail from '../../../../../ui/protectedEmail';
import PolicyContentSection, {
  SectionTitle,
  SectionContent,
  SubsectionText,
  ContactInfo,
  ContactItem,
  AddressBlock,
} from '../../../shared/components/PolicyContentSection';
import { IntroText, KeyPointsTitle, PolicyLink } from './styles';

const ContentSection = () => {
  return (
    <PolicyContentSection title="Terms of Service" lastUpdated="Last updated: December 17th, 2025">
      <Box sx={{ mb: 4 }}>
        <IntroText variant="body2" color="text.secondary">
          (For people using Nirva's hypnotherapy services – clients & prospective clients)
        </IntroText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>1. Overview</SectionTitle>
        <SectionContent>
          Welcome to Nirva – Interpersonal Hypnotherapy Clinic ("Nirva," "we," "our," or "us").
          These Terms of Service ("Terms") describe the conditions under which you may use Nirva's
          services, including:
        </SectionContent>
        <SubsectionText>
          • Hypnosis and hypnotherapy-facilitation services delivered by independent practitioners
          through Nirva
        </SubsectionText>
        <SubsectionText>
          • Telehealth visits conducted via SimplePractice Telehealth or other approved platforms
        </SubsectionText>
        <SubsectionText>
          • Related scheduling, billing, and communication tools provided by or on behalf of Nirva
        </SubsectionText>
        <SubsectionText>
          By booking, attending, or paying for a session through Nirva, you agree to these Terms,
          our{'\u00A0'}
          <PolicyLink
            href="https://dashboard.instituteofhypnotherapy.com/static/media/Client%20Participation%20Agreement%20&%20Client%20Bill%20of%20Rights.eee63c39d50cc450ad5f.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Client Participation Agreement
          </PolicyLink>
          , and our Client Privacy Notice. If you do not agree, please do not use our services.
        </SubsectionText>
        <SubsectionText>
          "Nirva," "we," and "our" include Nirva – Interpersonal Hypnotherapy Clinic and its owners,
          staff, and contracted practitioners.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>2. Nature of Services (Important Disclaimers)</SectionTitle>
        <SectionContent>
          Nirva provides hypnosis-based services rooted in Interpersonal Hypnotherapy and related
          personal-growth methods. Our services are intended to support:
        </SectionContent>
        <SubsectionText>• Stress management and relaxation</SubsectionText>
        <SubsectionText>• Habit change and mindset support</SubsectionText>
        <SubsectionText>• Performance, confidence, and personal development</SubsectionText>
        <SubsectionText>• Self-awareness and inner growth</SubsectionText>
        <SectionContent>
          Nirva is not an emergency service and is not a substitute for:
        </SectionContent>
        <SubsectionText>• Emergency medical care</SubsectionText>
        <SubsectionText>• Inpatient or crisis-level psychiatric care</SubsectionText>
        <SubsectionText>
          • Ongoing medical or mental health treatment from licensed professionals
        </SubsectionText>
        <SubsectionText>
          If you are in crisis or feel that you may harm yourself or someone else, do not use this
          website or our services. Call 911 or your local emergency number, or go to the nearest
          emergency room.
        </SubsectionText>
        <SubsectionText>
          Nothing on this site, in any session, or in our materials is medical, psychological,
          psychiatric, or legal advice.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>3. Eligibility & Client Responsibility</SectionTitle>
        <SectionContent>To use Nirva's services, you must:</SectionContent>
        <SubsectionText>
          • Be legally able to consent to services under the laws of your jurisdiction, or
        </SubsectionText>
        <SubsectionText>
          • Have a parent or legal guardian provide consent and participate as required for clients
          ages 14–17.
        </SubsectionText>
        <SectionContent>By using our services, you agree to:</SectionContent>
        <SubsectionText>
          • Provide accurate and complete information during intake and sessions
        </SubsectionText>
        <SubsectionText>
          • Inform your practitioner of important changes (e.g., medications, new diagnoses, safety
          concerns)
        </SubsectionText>
        <SubsectionText>
          • Ask questions if you do not understand any aspect of the services or these Terms
        </SubsectionText>
        <SubsectionText>• Use urgent and emergency resources appropriately</SubsectionText>
        <SectionContent>
          We reserve the right to decline, pause, or discontinue services when:
        </SectionContent>
        <SubsectionText>
          • Your needs are outside the scope of practice for Nirva or your practitioner
        </SubsectionText>
        <SubsectionText>• There are safety, ethical, or legal concerns</SubsectionText>
        <SubsectionText>
          • There is repeated non-attendance, nonpayment, or policy violations
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>4. Independent Practitioners</SectionTitle>
        <SectionContent>
          Nirva works with independent practitioners trained in Interpersonal Hypnotherapy.
          Practitioners are independent contractors, not employees, unless clearly stated otherwise.
        </SectionContent>
        <SectionContent>Each practitioner:</SectionContent>
        <SubsectionText>• Sets their own session fees within Nirva guidelines</SubsectionText>
        <SubsectionText>
          • Is responsible for their own professional judgment and documentation
        </SubsectionText>
        <SubsectionText>
          • Is required to carry professional liability insurance and to practice within the IAIH
          Scope of Practice and applicable laws
        </SubsectionText>
        <SubsectionText>
          • Nirva provides the platform, intake process, agreed workflows, and certain
          administrative services, but does not supervise every aspect of clinical judgment in real
          time.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>5. Scheduling, Fees & Cancellations</SectionTitle>
        <SectionContent>
          Session fees, payment terms, and cancellation/no-show rules are explained in:
        </SectionContent>
        <SubsectionText>• Your practitioner's profile and booking pages</SubsectionText>
        <SubsectionText>•{'\u00A0'}
          <PolicyLink
            href="https://www.canva.com/design/DAG3-J-tu0c/Mke1Haw42ey_-BRAAVj44Q/view?utlId=h32703d868a"
            target="_blank"
            rel="noopener noreferrer"
            >
             Nirva's Financial & Cancellations Policy
          </PolicyLink>
        </SubsectionText>
        <SubsectionText>• Your Client Participation Agreement</SubsectionText>
        <SectionContent>By booking an appointment, you agree to:</SectionContent>
        <SubsectionText>• Pay the fees disclosed to you at booking</SubsectionText>
        <SubsectionText>
          • Follow the cancellation and rescheduling rules (e.g., 24-hour notice)
        </SubsectionText>
        <SubsectionText>
          • Accept any late-cancel/no-show fees described in your intake materials
        </SubsectionText>
        <SubsectionText>
          • Nirva may use secure third-party processors to accept card payments. We do not store
          full card numbers on Nirva systems.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>6. Telehealth & Technology</SectionTitle>
        <SectionContent>
          Nirva primarily uses SimplePractice Telehealth or equivalent HIPAA-eligible telehealth
          platforms. By participating in telehealth, you agree to:
        </SectionContent>
        <SubsectionText>
          • Join sessions from a private space and use headphones when possible
        </SubsectionText>
        <SubsectionText>
          • Not record, screenshot, or save chat messages during sessions
        </SubsectionText>
        <SubsectionText>
          • Disclose anyone present in the room or within hearing range
        </SubsectionText>
        <SubsectionText>
          • Confirm your physical location (state/region) at the start of each telehealth visit
        </SubsectionText>
        <SubsectionText>
          • Practitioner-created hypnotic programming recordings may be offered for your personal
          use and are shared via secure methods. Any other recording requires specific authorization
          and Nirva approval.
        </SubsectionText>
        <SubsectionText>
          • We take reasonable steps to keep telehealth secure, but no system is perfectly secure
          and you accept some risk inherent in online communication.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>7. Privacy & HIPAA</SectionTitle>
        <SectionContent>
          Nirva follows HIPAA-aligned practices and uses secure systems for clinical information.
          Your information is handled according to:
        </SectionContent>
        <SubsectionText>•{'\u00A0'}
          <PolicyLink
            href="https://www.canva.com/design/DAG3-sQXze8/rWEn4R_suX3HP2wwQzJ0Dw/view?utm_content=DAG3-sQXze8&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h3873499d20"
            target="_blank"
            rel="noopener noreferrer"
          >
            Nirva Client Privacy Notice
          </PolicyLink>
        </SubsectionText>
        <SubsectionText>• Applicable state and federal privacy laws</SubsectionText>
        <KeyPointsTitle variant="body2" color="text.primary">
          Key points:
        </KeyPointsTitle>
        <SubsectionText>
          • We collect and use only the information needed for care and operations
        </SubsectionText>
        <SubsectionText>• We do not sell your information</SubsectionText>
        <SubsectionText>
          • We use Business Associate Agreements (BAAs) with vendors who may access Protected Health
          Information (PHI)
        </SubsectionText>
        <SubsectionText>
          • You have rights to access and request corrections to your records, as described in the
          Client Privacy Notice
        </SubsectionText>
        <SubsectionText>
          • For website visitor data (cookies, analytics, etc.), please see our Privacy Policy.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>8. Intellectual Property</SectionTitle>
        <SectionContent>
          All content on the Nirva site and platform—including text, graphics, logos, images, audio
          recordings, hypnotic programming recordings, and other materials—is owned by Nirva or its
          licensors, unless otherwise noted.
        </SectionContent>
        <SubsectionText>
          • You may use practitioner-created materials and recordings only for your personal use and
          may not copy, share, or sell them without written permission.
        </SubsectionText>
        <SubsectionText>
          • You may not reproduce, distribute, or create derivative works from any part of the Nirva
          site, platform, or materials without prior written consent.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>9. Prohibited Uses</SectionTitle>
        <SectionContent>You agree not to:</SectionContent>
        <SubsectionText>• Use Nirva's website or services for any unlawful purpose</SubsectionText>
        <SubsectionText>
          • Attempt to gain unauthorized access to systems or other users' information
        </SubsectionText>
        <SubsectionText>
          • Record or share sessions, screenshots, or chat logs without explicit written permission
        </SubsectionText>
        <SubsectionText>• Misrepresent your identity or impersonate someone else</SubsectionText>
        <SubsectionText>
          • We may suspend or terminate access if we believe you are violating these Terms or
          applicable laws.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>10. Limitation of Liability</SectionTitle>
        <SectionContent>To the fullest extent permitted by law:</SectionContent>
        <SubsectionText>
          • Nirva and its owners, staff, and practitioners are not liable for any indirect,
          incidental, special, consequential, or punitive damages, or for lost profits or data,
          arising from your use of our services or website.
        </SubsectionText>
        <SubsectionText>
          • Our total liability for any claim related to these Terms or the services will not exceed
          the amount you paid for the session(s) giving rise to the claim during the 3 months before
          the claim arose.
        </SubsectionText>
        <SubsectionText>
          • Nothing in this section limits liability where such limitation is prohibited by law (for
          example, some consumer protection laws).
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>11. Changes to Services or Terms</SectionTitle>
        <SectionContent>
          Nirva may update services, policies, and these Terms from time to time. When we make
          material changes:
        </SectionContent>
        <SubsectionText>
          • We will update the "Last updated" date at the top of this page
        </SubsectionText>
        <SubsectionText>
          • Continued use of services after changes means you accept the updated Terms
        </SubsectionText>
        <SubsectionText>
          • If you do not agree with changes, you may stop using the services at any time.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>12. Governing Law</SectionTitle>
        <SectionContent>
          These Terms are governed by the laws of the State of Florida, without regard to its
          conflict of law rules, unless the law of your state/region requires otherwise.
        </SectionContent>
      </Box>

      <Box sx={{ mb: 6 }}>
        <SectionTitle>13. Contact Us</SectionTitle>
        <SectionContent>
          For questions about these Terms or our services, please contact:
        </SectionContent>
        <ContactInfo>
          <ContactItem>
            Nirva Support:{' '}
            <ProtectedEmail email="support@mynirva.com" />
          </ContactItem>
          <ContactItem>
            Privacy & Security Officer:{' '}
            <ProtectedEmail email="privacy@mynirva.com" />
          </ContactItem>
          <ContactItem>Mailing address:</ContactItem>
          <AddressBlock>
            Nirva – Interpersonal Hypnotherapy Clinic
            <br />
            540 E. Horatio Avenue, Suite 200
            <br />
            Maitland, FL 32751
          </AddressBlock>
        </ContactInfo>
      </Box>
    </PolicyContentSection>
  );
};

export default ContentSection;
